import type { RouteComponentProps } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ListItem, ListItemContainer } from '@components/ListItem';
import { CircleLoader } from '@components/Loaders';
import MainTitle from '@components/MainTitle';
import Selector from '@components/Selector';

import { CLIENT_ID_PREFIX } from '@constants/client-id';

import { useGetVitallPatientByUserId } from '@hooks/api/vitall-patient';
import { useUpdateEmergencyAccess } from '@hooks/api/vitall-patient';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import AppLayout from '@layouts/AppLayout';
import { MainContainer } from '@layouts/DrilldownStyle';

import { formatFhirDate } from '@utils/fhir';
import { formatDate } from '@utils/format';

const messages = defineMessages({
  title: {
    id: 'emergencyAccessSettings.title',
    defaultMessage: 'Emergency Access',
  },
  everyTwoMonths: {
    id: 'emergencyAccessSettings.everyTwoMonths',
    defaultMessage: 'Enable emergency access',
  },
  enable: {
    id: 'emergencyAccessSettings.enable',
    defaultMessage: 'Enable emergency access',
  },
  vitallClientId: {
    id: 'emergencyAccessSettings.vitallClientId',
    defaultMessage: 'VITALL Client ID',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
  errorChange: {
    id: 'emergencyAccessSettings.errorChange',
    defaultMessage:
      'An error occured while changing emergency access visibility',
  },
  infoEnabled: {
    id: 'emergencyAccessSettings.infoEnabled',
    defaultMessage: 'Enabled emergency access',
  },
  infoDisabled: {
    id: 'emergencyAccessSettings.infoDisabled',
    defaultMessage: 'Disabled emergency access',
  },
  errorFetch: {
    id: 'emergencyAccessSettings.errorFetch',
    defaultMessage: 'Could not fetch emergency access details',
  },
});

const EmergencyAcccessSettings: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();

  const [enabled, setEnabled] = useState(false);
  const [clientNumber, setClientNumber] = useState('');

  const {
    state: { activeProfile },
  } = useSession();
  const { enqueueSnackbar } = useSnackbar();
  const [updateEmergencyAccess, updateEmergencyAccessLoading] =
    useUpdateEmergencyAccess();

  const { data: vitallPatientData, loading: vitallPatientLoading } =
    useGetVitallPatientByUserId();
  const vitallPatient = vitallPatientData?.getVitallPatientRecord;

  const loading = vitallPatientLoading || updateEmergencyAccessLoading;

  const dob = formatFhirDate(vitallPatient?.date_of_birth, {
    showTime: false,
    adjustTz: false,
  });

  useEffect(() => {
    if (!vitallPatient || !activeProfile?.vitall_user_id) {
      return;
    }
    setClientNumber(activeProfile.vitall_user_id.toString());
    setEnabled(vitallPatient.allow_emergency_access);
  }, [formatMessage, activeProfile, vitallPatient]);

  function zeroPad(num: number | string, places: number) {
    return String(num).padStart(places, '0');
  }

  async function toggleAccess(selected: boolean) {
    if (!vitallPatient?.vitall_patient_id) {
      return;
    }
    try {
      await updateEmergencyAccess({
        input: {
          vitall_patient_id: vitallPatient.vitall_patient_id,
          allow_emergency_access: selected,
        },
      });
      setEnabled(selected);
    } catch (error) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.errorChange),
      });
    }
  }

  return (
    <AppLayout friendlyName={formatMessage(messages.title)} showHeader>
      <MainContainer maxWidth="600px">
        <MainTitle title={formatMessage(messages.title)} />

        <ListItemContainer>
          <ListItem title={formatMessage(messages.enable)}>
            <CircleLoader loading={loading}>
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <Selector changeSelection={toggleAccess} isSelected={enabled} />
            </CircleLoader>
          </ListItem>

          {enabled && activeProfile ? (
            <>
              <ListItem
                description={`${CLIENT_ID_PREFIX}${zeroPad(clientNumber, 6)}`}
                title={formatMessage(messages.vitallClientId)}
              />
              <ListItem
                description={activeProfile.last_name}
                title={formatMessage(messages.lastName)}
              />
              <ListItem
                description={dob ? formatDate({ date: new Date(dob) }) : ''}
                title={formatMessage(messages.dob)}
              />
            </>
          ) : null}
        </ListItemContainer>
      </MainContainer>
    </AppLayout>
  );
};

export default EmergencyAcccessSettings;
